import { useCallback } from 'react';
import { useAngularContext } from 'AngularContext';
import { type AnyObject } from '@Types';
import { type TranslationHelperClass } from 'Translation';
import { type StreamForStreamDashboard } from 'Lessons';

/*
    Transcripts reminder after a foundations course (https://trello.com/c/oGP9fbb4). It stays on the Iguana
    user: the transcript getters have no vanilla fields, and the modal body is an Angular directive. It never
    sets has_seen_unofficial_transcripts_popup, so it shows on every foundations completion.
*/
type IguanaUserForTranscriptsCheck = {
    has_seen_unofficial_transcripts_popup: boolean;
    programType: string;
    emailDomain?: string;
    hasPendingProgramApplication: boolean;
    roleName: () => string;
    career_profile?: {
        numRequiredTranscriptsUploaded: number;
        numRequiredTranscriptsNotWaived: number;
    };
    relevantCohort: { foundations_lesson_stream_locale_pack_ids: string[] };
};

export function useUploadUnofficialTranscriptsModal(stream: StreamForStreamDashboard) {
    const $injector = useAngularContext();

    return useCallback(() => {
        const $rootScope = $injector.get<{ currentUser?: IguanaUserForTranscriptsCheck }>('$rootScope');
        const DialogModal = $injector.get<{ alert: (options: AnyObject) => void }>('DialogModal');
        const TranslationHelper = $injector.get<TranslationHelperClass>('TranslationHelper');
        const Cohort = $injector.get<{ supportsDocumentUpload: (programType: string) => boolean }>('Cohort');

        const currentUser = $rootScope.currentUser;

        if (
            !currentUser ||
            currentUser.has_seen_unofficial_transcripts_popup ||
            !currentUser.career_profile ||
            !Cohort.supportsDocumentUpload(currentUser.programType)
        ) {
            return;
        }

        const shouldShow =
            currentUser.roleName() === 'learner' &&
            currentUser.emailDomain !== 'pedago.com' &&
            currentUser.hasPendingProgramApplication &&
            currentUser.career_profile.numRequiredTranscriptsUploaded === 0 &&
            // The reminder is irrelevant when every required transcript has been waived.
            currentUser.career_profile.numRequiredTranscriptsNotWaived > 0 &&
            currentUser.relevantCohort.foundations_lesson_stream_locale_pack_ids.includes(stream.localePackId);

        if (!shouldShow) return;

        DialogModal.alert({
            title: new TranslationHelper('lessons.stream.upload_unofficial_transcripts_modal').get(
                'submit_your_transcripts',
            ),
            content: '<upload-unofficial-transcripts-modal></upload-unofficial-transcripts-modal>',
            size: 'small',
            classes: ['upload-unofficial-transcripts'],
            closeOnClick: false,
            hideCloseButton: true,
        });
    }, [stream, $injector]);
}
